'use client';

import { c as _c } from "react/compiler-runtime";
import { domAnimation, LazyMotion, m, useReducedMotion } from 'motion/react';
import { Slot } from 'radix-ui';
import { Children, isValidElement } from 'react';
import type { IconSvgElement } from '@/components/ui/icon';
import { Icon } from '@/components/ui/icon';
import { Loading03Icon } from '@/components/ui/icon/glyphs';
import { MOTION_HOVER, MOTION_TAP } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';

// ---------------------------------------------------------------------------
// Style constants
// ---------------------------------------------------------------------------

const variantClasses = {
  primary: 'bg-button-primary-bg text-button-primary-text hover:bg-button-primary-bg-hover focus-visible:ring-button-primary-ring/30',
  secondary: 'bg-background-tertiary text-foreground-primary hover:bg-background-tertiary/80 focus-visible:ring-border-primary/30',
  tertiary: 'bg-transparent text-foreground-secondary hover:bg-background-tertiary focus-visible:ring-border-primary/30',
  outline: 'border bg-transparent text-foreground-secondary hover:bg-background-tertiary focus-visible:ring-border-primary/30',
  destructive: 'bg-button-destructive-bg text-button-destructive-text hover:bg-button-destructive-bg-hover focus-visible:ring-button-destructive-ring/20'
} as const;
const labelSizeClasses = {
  default: 'h-9 gap-1.5 rounded-control text-body',
  sm: 'h-8 gap-1.5 rounded-control text-body',
  xs: 'h-6 gap-1 rounded-control text-caption',
  lg: 'h-10 gap-2 rounded-[0.875rem] text-body',
  xl: 'h-12 gap-2 rounded-[1rem] text-body-lg'
} as const;
const paddingLeft = {
  default: {
    full: 'pl-4',
    reduced: 'pl-3'
  },
  sm: {
    full: 'pl-3',
    reduced: 'pl-2.5'
  },
  xs: {
    full: 'pl-2',
    reduced: 'pl-1.5'
  },
  lg: {
    full: 'pl-6',
    reduced: 'pl-4'
  },
  xl: {
    full: 'pl-8',
    reduced: 'pl-6'
  }
} as const;
const paddingRight = {
  default: {
    full: 'pr-4',
    reduced: 'pr-3'
  },
  sm: {
    full: 'pr-3',
    reduced: 'pr-2.5'
  },
  xs: {
    full: 'pr-2',
    reduced: 'pr-1.5'
  },
  lg: {
    full: 'pr-6',
    reduced: 'pr-4'
  },
  xl: {
    full: 'pr-8',
    reduced: 'pr-6'
  }
} as const;
const iconSizeClasses = {
  default: 'size-icon-md',
  sm: 'size-icon-md',
  xs: 'size-icon-sm',
  lg: 'size-icon-lg',
  xl: 'size-icon-lg'
} as const;
const iconOnlySizeClasses = {
  default: 'aspect-square size-9 rounded-control',
  sm: 'aspect-square size-8 rounded-control',
  xs: 'aspect-square size-6 rounded-control',
  lg: 'aspect-square size-10 rounded-[0.875rem]',
  xl: 'aspect-square size-12 rounded-[1rem]'
} as const;
export type ButtonVariant = keyof typeof variantClasses;
export type ButtonSize = keyof typeof labelSizeClasses;
function renderableChildren(children: React.ReactNode): Array<React.ReactNode> {
  return Children.toArray(children).filter(child => child != null && typeof child !== 'boolean');
}
function isSingleIconElement(child: React.ReactNode): boolean {
  return isValidElement(child) && child.type === Icon;
}
function isSingleIconChild(children: React.ReactNode): boolean {
  const renderable = renderableChildren(children);
  return renderable.length === 1 && isSingleIconElement(renderable[0]);
}
const SPINNER_CLASS = 'animate-spin motion-reduce:animate-none' as const;

// ---------------------------------------------------------------------------
// Motion config
// ---------------------------------------------------------------------------

// Hover lift is now a CSS shadow transition, not a transform — scaling the
// button also scales the text glyphs, which causes a sub-pixel re-rasterise on
// every hover. Tap remains a Motion transform: it's brief and the press-down
// feel is worth the trade-off.
const TAP_SCALE = {
  scale: 0.97,
  transition: MOTION_TAP
} as const;
const HOVER_SHADOW_CLASS = 'hover:shadow-elevation-inset' as const;
const BASE_CLASSES = 'touch-target select-none appearance-none inline-flex shrink-0 items-center justify-center font-medium whitespace-nowrap outline-none cursor-pointer focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 transition-shadow duration-fast ease-default motion-reduce:transition-none' as const;

// Private CSS properties give Figma Dev Mode exact, code-owned references
// without promoting component geometry into the public Foundations scale.
const WEB_BUTTON_METRIC_CLASSES: string = ['[--web-button-content-color:currentColor]', '[--web-button-height-xs:24px]', '[--web-button-gap-xs:4px]', '[--web-button-padding-full-xs:8px]', '[--web-button-padding-reduced-xs:6px]', '[--web-button-radius-xs:12px]', '[--web-button-icon-xs:14px]', '[--web-button-height-sm:32px]', '[--web-button-gap-sm:6px]', '[--web-button-padding-full-sm:12px]', '[--web-button-padding-reduced-sm:10px]', '[--web-button-radius-sm:12px]', '[--web-button-icon-sm:16px]', '[--web-button-height-default:36px]', '[--web-button-gap-default:6px]', '[--web-button-padding-full-default:16px]', '[--web-button-padding-reduced-default:12px]', '[--web-button-radius-default:12px]', '[--web-button-icon-default:16px]', '[--web-button-height-lg:40px]', '[--web-button-gap-lg:8px]', '[--web-button-padding-full-lg:24px]', '[--web-button-padding-reduced-lg:16px]', '[--web-button-radius-lg:14px]', '[--web-button-icon-lg:20px]', '[--web-button-height-xl:48px]', '[--web-button-gap-xl:8px]', '[--web-button-padding-full-xl:32px]', '[--web-button-padding-reduced-xl:24px]', '[--web-button-radius-xl:16px]', '[--web-button-icon-xl:20px]', '[--web-button-focus-ring-width:3px]', '[--web-button-opacity-disabled:0.5]', '[--web-button-opacity-focus-ring:0.3]', '[--web-button-opacity-destructive-focus-ring:0.2]', '[--web-button-opacity-secondary-hover:0.8]'].join(' ');

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface AnimatedButtonProps {
  /** Visual style. @default 'primary' */
  variant?: ButtonVariant | undefined;
  /** Size derived from shadcn button scale. @default 'default' */
  size?: ButtonSize | undefined;
  /** Icon rendered before the label. */
  prefix?: IconSvgElement | undefined;
  /**
   * The action this button started is in flight. Shows the platform spinner in
   * the prefix slot (in place of `prefix`, or of the glyph on an icon-only
   * button), disables the button and marks it `aria-busy`. Swap the label for
   * an in-flight one ("Claiming…") where the copy has it.
   */
  loading?: boolean | undefined;
  /** Extra className merged onto the prefix Icon — use for `animate-spin` on
   *  pending-state spinners or other per-callsite icon styling. */
  prefixClassName?: string | undefined;
  /** Icon rendered after the label. */
  suffix?: IconSvgElement | undefined;
  /**
   * Label content. Icon-only chrome (square): use `prefix`/`suffix` without
   * children, or pass a single `<Icon />` child (no prefix/suffix).
   */
  children?: React.ReactNode | undefined;
  className?: string | undefined;
  disabled?: boolean | undefined;
  /** When true, skips the hover shadow lift. Tap feedback is unchanged. */
  disableHoverScale?: boolean | undefined;
  /** When true, skips tap scale. */
  disableTapScale?: boolean | undefined;
  /** @default 'button' */
  type?: 'button' | 'submit' | 'reset' | undefined;
  /** Associate the button with a `<form>` by id when it is rendered outside the form
   *  element. Used by modal footers whose submit button lives in the SilkSheet `footer`
   *  slot but submits the form in the body. Only valid alongside `type="submit"`. */
  form?: string | undefined;
  onClick?: React.MouseEventHandler<HTMLButtonElement> | undefined;
  onPointerEnter?: React.PointerEventHandler<HTMLButtonElement> | undefined;
  onPointerLeave?: React.PointerEventHandler<HTMLButtonElement> | undefined;
  onMouseEnter?: React.MouseEventHandler<HTMLButtonElement> | undefined;
  onMouseLeave?: React.MouseEventHandler<HTMLButtonElement> | undefined;
  onFocus?: React.FocusEventHandler<HTMLButtonElement> | undefined;
  onBlur?: React.FocusEventHandler<HTMLButtonElement> | undefined;
  /**
   * Render via Radix `Slot.Root` so the chrome class set is forwarded onto a
   * single child element (e.g. `<Link>`, `<a>`, `<DialogClose>`). Motion is
   * suppressed in this branch — the child element manages its own behaviour
   * and Radix Slot cannot drive motion props on arbitrary children.
   * @default false
   */
  asChild?: boolean | undefined;
  'aria-label'?: string | undefined;
  'aria-describedby'?: string | undefined;
  'aria-expanded'?: boolean | undefined;
  'aria-haspopup'?: React.AriaAttributes['aria-haspopup'] | undefined;
  'aria-pressed'?: boolean | undefined;
  'aria-controls'?: string | undefined;
  'aria-current'?: React.AriaAttributes['aria-current'] | undefined;
  'data-testid'?: string | undefined;
  'data-simulation-cta'?: string | undefined;
  id?: string | undefined;
  name?: string | undefined;
  tabIndex?: number | undefined;
  title?: string | undefined;
  role?: string | undefined;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/** Morfi's canonical button wrapper. */
export function AnimatedButton(t0) {
  const $ = _c(89);
  let aria;
  let children;
  let className;
  let disableHoverScale;
  let disableTapScale;
  let disabledProp;
  let form;
  let onBlur;
  let onClick;
  let onFocus;
  let onMouseEnter;
  let onMouseLeave;
  let onPointerEnter;
  let onPointerLeave;
  let prefixClassNameProp;
  let prefixProp;
  let suffix;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== t0) {
    ({
      variant: t1,
      size: t2,
      loading: t3,
      prefix: prefixProp,
      prefixClassName: prefixClassNameProp,
      suffix,
      children,
      className,
      disabled: disabledProp,
      disableHoverScale,
      disableTapScale,
      type: t4,
      form,
      onClick,
      onPointerEnter,
      onPointerLeave,
      onMouseEnter,
      onMouseLeave,
      onFocus,
      onBlur,
      asChild: t5,
      ...aria
    } = t0);
    $[0] = t0;
    $[1] = aria;
    $[2] = children;
    $[3] = className;
    $[4] = disableHoverScale;
    $[5] = disableTapScale;
    $[6] = disabledProp;
    $[7] = form;
    $[8] = onBlur;
    $[9] = onClick;
    $[10] = onFocus;
    $[11] = onMouseEnter;
    $[12] = onMouseLeave;
    $[13] = onPointerEnter;
    $[14] = onPointerLeave;
    $[15] = prefixClassNameProp;
    $[16] = prefixProp;
    $[17] = suffix;
    $[18] = t1;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
  } else {
    aria = $[1];
    children = $[2];
    className = $[3];
    disableHoverScale = $[4];
    disableTapScale = $[5];
    disabledProp = $[6];
    form = $[7];
    onBlur = $[8];
    onClick = $[9];
    onFocus = $[10];
    onMouseEnter = $[11];
    onMouseLeave = $[12];
    onPointerEnter = $[13];
    onPointerLeave = $[14];
    prefixClassNameProp = $[15];
    prefixProp = $[16];
    suffix = $[17];
    t1 = $[18];
    t2 = $[19];
    t3 = $[20];
    t4 = $[21];
    t5 = $[22];
  }
  const variant = t1 === undefined ? "primary" : t1;
  const size = t2 === undefined ? "default" : t2;
  const loading = t3 === undefined ? false : t3;
  const type = t4 === undefined ? "button" : t4;
  const asChild = t5 === undefined ? false : t5;
  const prefersReduced = useReducedMotion();
  const disabled = disabledProp === true || loading;
  const prefix = loading ? Loading03Icon : prefixProp;
  let t6;
  if ($[23] !== loading || $[24] !== prefixClassNameProp) {
    t6 = loading ? cn(prefixClassNameProp, SPINNER_CLASS) : prefixClassNameProp;
    $[23] = loading;
    $[24] = prefixClassNameProp;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  const prefixClassName = t6;
  let t7;
  if ($[26] !== onBlur || $[27] !== onClick || $[28] !== onFocus || $[29] !== onMouseEnter || $[30] !== onMouseLeave || $[31] !== onPointerEnter || $[32] !== onPointerLeave) {
    t7 = {
      onClick,
      onPointerEnter,
      onPointerLeave,
      onMouseEnter,
      onMouseLeave,
      onFocus,
      onBlur
    };
    $[26] = onBlur;
    $[27] = onClick;
    $[28] = onFocus;
    $[29] = onMouseEnter;
    $[30] = onMouseLeave;
    $[31] = onPointerEnter;
    $[32] = onPointerLeave;
    $[33] = t7;
  } else {
    t7 = $[33];
  }
  const handlers = t7;
  const hasPrefix = prefix !== undefined;
  const hasSuffix = suffix !== undefined;
  let t8;
  if ($[34] !== children || $[35] !== loading || $[36] !== prefixProp || $[37] !== suffix) {
    t8 = loading && isSingleIconChild(children) && prefixProp === undefined && suffix === undefined;
    $[34] = children;
    $[35] = loading;
    $[36] = prefixProp;
    $[37] = suffix;
    $[38] = t8;
  } else {
    t8 = $[38];
  }
  const iconOnlyChild = t8;
  let t9;
  let usesGlyphIconOnly;
  if ($[39] !== children || $[40] !== hasPrefix || $[41] !== hasSuffix || $[42] !== iconOnlyChild) {
    const nonWhitespaceChildren = iconOnlyChild ? [] : renderableChildren(children);
    usesGlyphIconOnly = nonWhitespaceChildren.length === 0 && (hasPrefix || hasSuffix);
    t9 = nonWhitespaceChildren.length === 1 && isSingleIconElement(nonWhitespaceChildren[0]) && !hasPrefix && !hasSuffix;
    $[39] = children;
    $[40] = hasPrefix;
    $[41] = hasSuffix;
    $[42] = iconOnlyChild;
    $[43] = t9;
    $[44] = usesGlyphIconOnly;
  } else {
    t9 = $[43];
    usesGlyphIconOnly = $[44];
  }
  const usesSingleIconChild = t9;
  const isIconOnly = usesGlyphIconOnly || usesSingleIconChild;
  const iconClass = iconSizeClasses[size];
  let t10;
  if ($[45] !== className || $[46] !== disableHoverScale || $[47] !== disabled || $[48] !== hasPrefix || $[49] !== hasSuffix || $[50] !== isIconOnly || $[51] !== size || $[52] !== variant) {
    const sizeClass = isIconOnly ? iconOnlySizeClasses[size] : cn(labelSizeClasses[size], paddingLeft[size][hasPrefix ? "reduced" : "full"], paddingRight[size][hasSuffix ? "reduced" : "full"]);
    t10 = cn(BASE_CLASSES, WEB_BUTTON_METRIC_CLASSES, variantClasses[variant], sizeClass, disableHoverScale !== true && disabled !== true && HOVER_SHADOW_CLASS, className);
    $[45] = className;
    $[46] = disableHoverScale;
    $[47] = disabled;
    $[48] = hasPrefix;
    $[49] = hasSuffix;
    $[50] = isIconOnly;
    $[51] = size;
    $[52] = variant;
    $[53] = t10;
  } else {
    t10 = $[53];
  }
  const composedClassName = t10;
  const glyphIconOnly = usesGlyphIconOnly ? prefix ?? suffix : undefined;
  let t11;
  if ($[54] !== children || $[55] !== glyphIconOnly || $[56] !== hasPrefix || $[57] !== hasSuffix || $[58] !== iconClass || $[59] !== prefix || $[60] !== prefixClassName || $[61] !== suffix) {
    t11 = glyphIconOnly !== undefined ? <Icon icon={glyphIconOnly} className={cn(iconClass, "shrink-0", glyphIconOnly === prefix && prefixClassName)} /> : <>{hasPrefix && <Icon icon={prefix} className={cn(iconClass, "shrink-0", prefixClassName)} />}{children}{hasSuffix && <Icon icon={suffix} className={cn(iconClass, "shrink-0")} />}</>;
    $[54] = children;
    $[55] = glyphIconOnly;
    $[56] = hasPrefix;
    $[57] = hasSuffix;
    $[58] = iconClass;
    $[59] = prefix;
    $[60] = prefixClassName;
    $[61] = suffix;
    $[62] = t11;
  } else {
    t11 = $[62];
  }
  const content = t11;
  if (asChild) {
    let t12;
    if ($[63] !== aria || $[64] !== children || $[65] !== composedClassName) {
      t12 = <Slot.Root className={composedClassName} {...aria}>{children}</Slot.Root>;
      $[63] = aria;
      $[64] = children;
      $[65] = composedClassName;
      $[66] = t12;
    } else {
      t12 = $[66];
    }
    return t12;
  }
  if (prefersReduced) {
    const t12 = loading || undefined;
    let t13;
    if ($[67] !== aria || $[68] !== composedClassName || $[69] !== content || $[70] !== disabled || $[71] !== form || $[72] !== handlers || $[73] !== t12 || $[74] !== type) {
      t13 = <button className={composedClassName} disabled={disabled} aria-busy={t12} type={type} form={form} {...handlers} {...aria}>{content}</button>;
      $[67] = aria;
      $[68] = composedClassName;
      $[69] = content;
      $[70] = disabled;
      $[71] = form;
      $[72] = handlers;
      $[73] = t12;
      $[74] = type;
      $[75] = t13;
    } else {
      t13 = $[75];
    }
    return t13;
  }
  let t12;
  if ($[76] !== disableTapScale || $[77] !== disabled) {
    t12 = disabled === true ? {} : {
      ...(disableTapScale === true ? {} : {
        whileTap: TAP_SCALE
      })
    };
    $[76] = disableTapScale;
    $[77] = disabled;
    $[78] = t12;
  } else {
    t12 = $[78];
  }
  const motionProps = t12;
  const t13 = loading || undefined;
  let t14;
  if ($[79] !== aria || $[80] !== composedClassName || $[81] !== content || $[82] !== disabled || $[83] !== form || $[84] !== handlers || $[85] !== motionProps || $[86] !== t13 || $[87] !== type) {
    t14 = <LazyMotion features={domAnimation}><m.button {...motionProps} transition={MOTION_HOVER} className={composedClassName} disabled={disabled} aria-busy={t13} type={type} form={form} {...handlers} {...aria}>{content}</m.button></LazyMotion>;
    $[79] = aria;
    $[80] = composedClassName;
    $[81] = content;
    $[82] = disabled;
    $[83] = form;
    $[84] = handlers;
    $[85] = motionProps;
    $[86] = t13;
    $[87] = type;
    $[88] = t14;
  } else {
    t14 = $[88];
  }
  return t14;
}